import _extends from "@babel/runtime/helpers/extends";
import React, { memo, useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import AnimateHeight from 'react-animate-height';
import Icon from '../../general/Icon';
import BadgeNotification from '../../general/BadgeNotification';
import Text from '../../typography/Text';
import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles } from '../../../utils/styles';
import styles from './styles';
const useStyles = createUseStyles(styles, 'Section');
var _ref2 = /*#__PURE__*/React.createElement(Icon, {
  name: "arrowDropDown"
});
const Section = /*#__PURE__*/memo(_ref => {
  let {
    isOpen: isOpenProp,
    children,
    classes: classesProp,
    overrides: overridesProp,
    className: classNameProp,
    title,
    isExpandable = true,
    defaultOpen = true,
    onChange,
    onRemove,
    activeFields,
    orientation,
    headerPreComponent,
    headerPostComponent
  } = _ref;
  const classes = useClasses(useStyles, classesProp);
  const [isOpen, setIsOpen] = useState(onChange ? isOpenProp : defaultOpen);
  useEffect(() => {
    if (onChange) setIsOpen(isOpenProp);
  }, [isOpenProp, onChange]);
  const override = getOverrides(overridesProp, Section.overrides);
  const rootClassName = classnames(classes.root, {
    [classes.horizontal]: orientation === 'horizontal'
  }, classNameProp);
  const headerClassName = classnames(classes.header, {
    [classes.isExpandable]: isExpandable,
    [classes.open]: isOpen,
    [classes.headerActiveFields]: !!activeFields,
    [classes.withRemove]: !!onRemove
  });
  const onToggle = useCallback(() => {
    onChange && onChange(!isOpen);
    !onChange && setIsOpen(!isOpen);
  }, [isOpen, onChange]);
  const onInnerRemove = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    onRemove && onRemove();
  }, [onRemove]);
  const renderTitle = useMemo(() => {
    if (typeof title === 'string') {
      return /*#__PURE__*/React.createElement("div", _extends({
        className: classes.textContainer
      }, override.textContainer), /*#__PURE__*/React.createElement(Text, _extends({
        medium: true,
        type: "subtitle",
        className: classes.Text
      }, override.Text), title));
    }
    return title;
  }, [classes.Text, classes.textContainer, override.Text, override.textContainer, title]);
  const newActiveFields = useMemo(() => {
    if (!activeFields || isOpen) return null;
    return /*#__PURE__*/React.createElement(BadgeNotification, _extends({
      size: "small",
      className: classes.BadgeNotification
    }, override.BadgeNotification), activeFields);
  }, [classes, override, activeFields, isOpen]);
  if (title && isExpandable) {
    return /*#__PURE__*/React.createElement("div", _extends({
      className: rootClassName
    }, override.root), /*#__PURE__*/React.createElement("div", _extends({
      className: headerClassName,
      onClick: onToggle
    }, override.header), /*#__PURE__*/React.createElement("div", _extends({
      className: classes.headerContent
    }, override.headerContent), headerPreComponent, /*#__PURE__*/React.createElement("div", _extends({
      className: classes.titleContainer
    }, override.titleContainer), /*#__PURE__*/React.createElement("div", _extends({
      className: classes.icon
    }, override.icon), _ref2), renderTitle), newActiveFields, headerPostComponent, onRemove && /*#__PURE__*/React.createElement(Icon, {
      onClick: onInnerRemove,
      className: classes.trashIcon,
      name: "delete"
    }))), /*#__PURE__*/React.createElement(AnimateHeight, _extends({
      height: isOpen ? 'auto' : 0
    }, override['react-animate-height']), children));
  } else if (title && !isExpandable) {
    return /*#__PURE__*/React.createElement("div", _extends({
      className: rootClassName
    }, override.root), /*#__PURE__*/React.createElement("div", _extends({
      className: headerClassName
    }, override.header), /*#__PURE__*/React.createElement("div", _extends({
      className: classes.headerContent
    }, override.headerContent), /*#__PURE__*/React.createElement("div", _extends({
      className: classes.titleContainer
    }, override.titleContainer), renderTitle), newActiveFields)), children);
  }
  return /*#__PURE__*/React.createElement("div", _extends({
    className: rootClassName
  }, override.root), children);
});
Section.overrides = ['root', 'header', 'Text', 'BadgeNotification', 'icon', 'react-animate-height'];
process.env.NODE_ENV !== "production" ? Section.propTypes = {
  /** Custom CSS class for styling the root element */
  className: PropTypes.string,
  /** Title displayed in the section header. Can be a string or custom component */
  title: PropTypes.any,
  /** Component to render before the title in the header */
  headerPreComponent: PropTypes.any,
  /** Component to render after the title in the header */
  headerPostComponent: PropTypes.any,
  /** Determines if the section is initially open when in uncontrolled mode */
  defaultOpen: PropTypes.bool,
  /** When true, allows the section to be expanded and collapsed */
  isExpandable: PropTypes.bool,
  /** Number of active fields to display in a badge when the section is collapsed */
  activeFields: PropTypes.number,
  /** Callback triggered when the section is expanded or collapsed. Makes the component controlled */
  onChange: PropTypes.func,
  /** Callback triggered when the remove icon is clicked */
  onRemove: PropTypes.func,
  /** Controls the open/closed state when in controlled mode */
  isOpen: PropTypes.bool,
  /** Object with custom style overrides */
  overrides: PropTypes.shape({
    /** Styles applied to the root element */
    root: PropTypes.object,
    /** Styles applied to the header element */
    header: PropTypes.object,
    /** Styles applied to the title text component */
    Text: PropTypes.object,
    /** Styles applied to the dropdown icon */
    icon: PropTypes.object,
    /** Styles applied to the badge notification component */
    BadgeNotification: PropTypes.object,
    /** Styles applied to the animation container */
    'react-animate-height': PropTypes.object,
    /** Styles applied to the text container */
    textContainer: PropTypes.object,
    /** Styles applied to the title container */
    titleContainer: PropTypes.object,
    /** Styles applied to the header content container */
    headerContent: PropTypes.object
  }),
  /** Orientation of the section content ('horizontal' or 'vertical') */
  orientation: PropTypes.oneOf(['horizontal', 'vertical'])
} : void 0;
export default Section;